// Third-party
import React, { Component } from 'react';
import { Route, Switch, Redirect, withRouter, RouteComponentProps } from 'react-router-dom';
import { CompatRouter } from 'react-router-dom-v5-compat';
import { connect } from 'react-redux';
import classNames from 'classnames';
import PerfectScrollbar from 'react-perfect-scrollbar';
import qs from 'qs';
import store from '@/redux/store';
import { StatsigProvider } from '@statsig/react-bindings';
import { configureStatsigClient, getStatsigClient } from '@/utils/statsig-client';
import { AppProvider } from '@capintel/component-lib/app-provider';
import '@capintel/component-lib/style';

// Components
import {
  Auth,
  Error,
  Contacts,
  ContactDetails,
  Search,
  Models,
  Settings,
  PinnedEditPortfolio,
  Insights,
  Admin,
  OAuth2,
  OAuth2Success,
  Announcements,
  Comparisons,
  ComparisonBuilder,
} from '.';
import PrivateRoute from '../components/PrivateRoute';
import DevelopmentRoute from '../components/DevelopmentRoute';
import ConnectedPrivateComponent from '../components/PrivateComponent';
import ExternalNotificationCenterProvider from './notification-center/ExternalNotificationCenterProvider';
import ProposalTemplateRouter from '../../modules/proposal/components/router/ProposalTemplateRouter';
import { Enable2FAModal } from './auth/2fa';
import NotificationCenter from '../components/notification-center';
import DevToolPanel from '../components/dev-tools/DevToolPanel';
import ImportAccountsPage from './planning/ImportAccountsPage';
import AssignContactPage from './planning/AssignContactPage';
import HouseholdLanding from '@/views/containers/households/HouseholdLanding';
import TestsRouter from '../components/dev-tools/TestsRouter';
import RouteListener from '../../route-listener';
import { BodyClassManager } from '../components/BodyClassManager';

// Redux
import { ReduxState } from '../../redux/modules/types';
import { getSavedFunds } from '../../redux/modules/funds/saved-funds';
import { loadTaxes } from '../../redux/modules/taxes';
import { getSavedBenchmarks } from '../../redux/modules/comparison-benchmarks/saved-benchmarks';
import { getMe, validateUser, updatePreferences, updateTheme } from '../../redux/modules/auth';
import { getContactById, loadContactData } from '../../redux/modules/contacts';
import { setEnterpriseDetails } from '../../redux/modules/enterprise';
import { setRateLimits } from '../../redux/modules/rate-limits';
import { RateLimits } from '../../redux/modules/types/rate-limits';
import { CurrentContact } from '../../redux/modules/types/contacts';
import { setLocale, addMessages } from '../../redux/modules/intl';
import { setSelectedDelegateAccess, validateDelegateAccessToken } from '../../redux/modules/delegates';
import { CountryTaxRates } from '../../redux/modules/types/taxe-rates';
import { showModal, hideModal } from '../../redux/modules/two-factor-auth';

// Utils
import domUtils from '../../utils/dom';
import LogRocketUtils from '../../utils/3rd-party/logrocketUtils';
import ZendeskUtils, { ZENDESK_FIELDS } from '../../utils/3rd-party/zendesk';
import ChargebeeUtils from '../../utils/3rd-party/chargebee';
import heapUtils from '../../utils/3rd-party/heap';
import datadogUtils from '@/utils/3rd-party/datadogUtils';
import sentryUtils from '../../utils/3rd-party/sentry';
import productGuideUtils from '@/utils/3rd-party/product-guide';
import cacheUtils from '../../utils/cache';
import { COMPARISON_NAV_PATHS, NAV_PATHS } from '../../utils/constants';
import { userHasPermission, PERMISSIONS, userHasPermissions, userHasPermissionEnabled, ENTERPRISE_ADMIN_PERMISSIONS } from '../../utils/permissions';
import sessionStorage, { SessionStorageKey } from '../../utils/session-storage';
import User from '@/types/user';
import Locale from '@/types/locale';
import Comparison from '@/types/comparison';
import Nullable from '@/types/nullable';
import EnterpriseDetails from '@/types/enterprise-detail-info';
import AdvisorContext from '@/types/advisor-context';
import Fund from '@/types/fund';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import GenericContact from '@/types/generic-contact';
import MessageSet from '@/types/message-set';
import AppMode from '@/types/app-mode';
import ThemeMode from '@/types/theme-mode';
import UserPreferences from '@/types/user-preferences';
import { PostMessage } from '../components/post-messages/PostMessage';
import { PostMessageSender } from '@/utils/post-messages/outgoing-messages/post-message-sender';
import { ITEM_COLORS_THEME_LIGHT, overrideComparisonItemColors } from '@/utils/colors';
import { RISK_COLORS_THEME_LIGHT, ACCESSIBLE_RISK_COLORS, overrideDefaultRiskColors } from '@/utils/risk-profiles';
import { getValidLocale } from '@/utils/locale';
import { getTheme, getThemeVersion } from '@/utils/theme';
import HouseholdContactDetails from './households/contact-details/HouseholdContactDetails';
import { setIsActiveContactPostMessage, setIsActivePortfolioPostMessage, setIsActivePostMessage } from '@/redux/modules/post-message';
import { handleSSOPopup } from '@/utils/auth';
import { SSOComplete } from './auth/SSOComplete';
import ErrorPage from './planning/ErrorPage';
import localStorage from '@/utils/local-storage';

type StateProps = {
  user?: User | null;
  locale: Locale;
  isPinned: boolean;
  isPostMessageActive: boolean;
  comparison?: Comparison;
  skipEmptyState: boolean;
  contact?: Nullable<CurrentContact>;
  enterprise: EnterpriseDetails;
  context: Nullable<AdvisorContext>;
  taxRates: Nullable<CountryTaxRates>;
  showTwoFactorModal: boolean;
  userPreferences?: UserPreferences;
};

type DispatchProps = {
  getSavedFunds: () => Promise<Fund[]>;
  getSavedBenchmarks: () => Promise<ComparisonBenchmark[]>;
  getMe: (contextId?: string) => Promise<User | null>;
  validateUser: (token: string) => Promise<boolean>;
  setEnterpriseDetails: (details: EnterpriseDetails) => void;
  setRateLimits: (rates?: RateLimits) => void;
  getContactById: (id: string) => Promise<GenericContact>;
  loadContactData: (contact: GenericContact) => Promise<void>;
  setLocale: (locale: Locale) => void;
  addMessages: (messages: MessageSet) => void;
  loadTaxes: () => void;
  setSelectedDelegateAccess: (id: string) => Promise<void>;
  validateDelegateAccessToken: (token: string) => Promise<string>;
  updatePreferences: (preferences: Partial<UserPreferences>) => Promise<void>;
  updateTheme: (theme: ThemeMode) => void;
  showModal: () => void;
  hideModal: () => void;
  setIsActivePostMessage: () => void;
  setIsActiveContactPostMessage: (isActive: boolean) => void;
  setIsActivePortfolioPostMessage: (isActive: boolean) => void;
};

type Props = StateProps & DispatchProps & RouteComponentProps;

type State = {
  initialized: boolean;
  postMessageReady: boolean;
};

class App extends Component<Props, State> {
  FULL_HEIGHT_PAGES = [NAV_PATHS.INSIGHTS, NAV_PATHS.CONTACTS, NAV_PATHS.AUTH];

  constructor(props: Props) {
    super(props);

    this.state = {
      initialized: !!props.user,
      postMessageReady: false,
    };

    if (props.user && domUtils.appLoaderIsVisible()) {
      domUtils.removeAppLoader();
    }
  }

  async componentDidMount() {
    window.addEventListener(ZendeskUtils.WIDGET_AUTHENTICATED, this.onZendeskAuthentication);

    await cacheUtils.bust();

    // Init Datadog before login to capture errors outside of private routes
    datadogUtils.init();

    ChargebeeUtils.init();

    const { user, location } = this.props;

    await configureStatsigClient(user);

    if (user === undefined) {
      const {
        token,
        hash,
        'comparison-token': comparisonToken,
        contextId: queryContextId,
        timeoutUrl: queryTimeoutUrl,
        logoutUrl: queryLogoutUrl,
        locale,
        ssoRedirect,
        callbackUrl,
      } = qs.parse(location.search, { ignoreQueryPrefix: true });

      // Edge case if the user was trying to use an invite link to access a comparison
      if (hash && token && comparisonToken) {
        this.props.validateUser(comparisonToken as string).then(isOnboarded => {
          if (isOnboarded) {
            if (comparisonToken) {
              this.props.history.push(`${NAV_PATHS.COMPARISON}?comparison-token=${comparisonToken}`);
            }
          }
        });
      }

      if (locale != null) {
        this.props.setLocale(locale as Locale);
      }

      let contextId = queryContextId as string | undefined;
      if (contextId != null) {
        const oldContextId = sessionStorage.getItem(SessionStorageKey.CONTEXT_ID);
        if (contextId !== oldContextId) {
          sessionStorage.setItem(SessionStorageKey.CONTEXT_ID, contextId);
          if (queryTimeoutUrl != null) {
            sessionStorage.setItem(SessionStorageKey.TIMEOUT_URL, queryTimeoutUrl as string);
          }
        }
      } else {
        contextId = sessionStorage.getItem(SessionStorageKey.CONTEXT_ID);
      }

      if (queryLogoutUrl != null) {
        sessionStorage.setItem(SessionStorageKey.LOGOUT_URL, queryLogoutUrl as string);
      } else {
        sessionStorage.removeItem(SessionStorageKey.LOGOUT_URL);
      }

      if (ssoRedirect && callbackUrl) {
        handleSSOPopup(ssoRedirect as string, callbackUrl as string);
      }
      try {
        await this.getContextInfo(contextId);
      } finally {
        this.setState({ initialized: true });
      }
    } else {
      this.setState({ initialized: true });
    }
  }

  async componentDidUpdate(prevProps: Props, prevState: State) {
    const {
      user,
      locale,
      isPinned,
      enterprise,
      taxRates,
      context,
      location,
      setIsActivePostMessage,
      setIsActiveContactPostMessage,
      setIsActivePortfolioPostMessage,
    } = this.props;
    const { initialized, postMessageReady } = this.state;

    // Configure Statsig client when user becomes available or when user changes
    if (prevProps.user !== user) {
      await configureStatsigClient(user);
    }

    if (prevProps.user == null && taxRates == null && user != null && userHasPermission(user, PERMISSIONS.TAXES)) {
      this.props.loadTaxes();
    }

    if (user && !postMessageReady) {
      const { 'post-message': postMessage } = qs.parse(location.search, { ignoreQueryPrefix: true });
      if (postMessage === 'true') {
        setIsActivePostMessage();
        if (userHasPermission(user, PERMISSIONS.COMPARISONS_LOAD_FACTSET_PORTFOLIOS)) {
          setIsActiveContactPostMessage(false);
          setIsActivePortfolioPostMessage(false);
        }
      }
      PostMessageSender.ready(user, store.getState);
      this.setState({ postMessageReady: true });
    }

    if (initialized && (!prevState.initialized || domUtils.appLoaderIsVisible())) {
      const { portfolioId, contactId, 'post-message': postMessage } = qs.parse(location.search, { ignoreQueryPrefix: true });
      if (location.pathname === NAV_PATHS.COMPARISON && ((portfolioId != null && contactId != null) || postMessage === 'true')) {
        // We are loading a contact portfolio straight into a new comparison (Salesforce demo).
        // We will remove the AppLoader once everything is ready. We may want to also do this
        // when going directly for a comparison (/comparison/:comparisonId/)
      } else {
        domUtils.removeAppLoader();
      }
      ZendeskUtils.setLocale(locale);
    }

    if (user && prevProps.user !== user) {
      const localeForApp = this.getAppLocale(user);
      if (this.props.locale !== localeForApp) {
        this.props.setLocale(localeForApp);
      }

      this.props.getSavedFunds();

      this.props.setEnterpriseDetails(user.enterprise);

      localStorage.clearProposalBrandingInfo();

      if (user.appConfig.whitelabel) {
        this.props.addMessages(user.appConfig.whitelabel);
      }

      if (userHasPermission(user, PERMISSIONS.BENCHMARKS)) {
        this.props.getSavedBenchmarks();
      }

      if (!userHasPermission(user, PERMISSIONS.CHAT_SUPPORT)) {
        // Explicitly hide widget since authenticated user doesn't have access to
        // chat support but Zendesk initialization displays it by default
        console.info('CHAT - Access denied');
        ZendeskUtils.hideWidget();
      } else {
        console.info('CHAT - Access granted. Launching.');
        ZendeskUtils.authenticateZendeskMessenger(localeForApp);
      }

      // Datadog and LogRocket are mutually exclusive
      if (userHasPermission(user, PERMISSIONS.DATADOG_SESSION_REPLAY)) {
        datadogUtils.identify(user);
        const sessionUrl = datadogUtils.getSessionURL();
        if (sessionUrl) {
          sentryUtils.setSessionUrl(sessionUrl);
        }
      } else {
        // TODO -- Cleanup once we get rid of LogRocket
        datadogUtils.shutdown();
        LogRocketUtils.init();
        LogRocketUtils.identify(user);
        LogRocketUtils.getSessionUrl(sessionUrl => {
          sentryUtils.setSessionUrl(sessionUrl);
        });
      }

      if (userHasPermission(user, PERMISSIONS.PRODUCT_GUIDE)) {
        productGuideUtils.init();
        productGuideUtils.identify(user);
      }

      heapUtils.identify(user, localeForApp, enterprise, context);
      sentryUtils.identify(user);

      domUtils.adjustPrintForUser();
    }

    if (user && enterprise !== prevProps.enterprise) {
      heapUtils.identify(user, locale, enterprise, context);
    }

    if (user && context !== prevProps.context) {
      heapUtils.identify(user, locale, enterprise, context);
    }

    if (isPinned !== prevProps.isPinned) {
      domUtils.fireResizeEvent();
    }

    if (user?.appConfig.mode) {
      this.handleTheme(prevProps, user?.appConfig.mode);
    }
  }

  componentWillUnmount() {
    window.removeEventListener(ZendeskUtils.WIDGET_AUTHENTICATED, this.onZendeskAuthentication);
  }

  getAppLocale = (user: User) => {
    const { locale: stateLocale } = this.props;
    const {
      authUser: { locale: authUserLocale },
      appConfig: { appLocales },
    } = user;

    return getValidLocale(authUserLocale, stateLocale, appLocales);
  };

  validateDelegateToken = async () => {
    const { location } = this.props;
    const query = qs.parse(location.search, { ignoreQueryPrefix: true });
    const { 'delegate-access-token': delegateAccessToken } = query;

    if (!delegateAccessToken) {
      return;
    }

    try {
      const id = await this.props.validateDelegateAccessToken(delegateAccessToken as string);
      await this.props.setSelectedDelegateAccess(id);

      // There might be other query parameters - let's first `consume` the delegate
      // token, and then we'll let the app consume everything else.
      delete query['delegate-access-token'];
      window.location.href = `${location.pathname}?${qs.stringify(query)}`;
    } catch (err) {
      // If status is 401, we simply aren't authenticated yet - we'll validate delegate access token later.
      // In that case, eat up the error - otherwise throw it back.
      if ((err as any).status !== 401) {
        throw err;
      }
    }
  };

  // Due to odd redraw behaviors of the Zendesk Web Widget Core API
  // after authentication an event is dispatched. After this event dispatch,
  // the web widget is shown to the user with an extra delay (WIDGET_DELAY)
  onZendeskAuthentication = () => {
    const { user } = this.props;

    if (user && userHasPermission(user, PERMISSIONS.CHAT_SUPPORT)) {
      setTimeout(() => {
        ZendeskUtils.showWidget();
        ZendeskUtils.updateConversationField(ZENDESK_FIELDS.ENTERPRISE, user.enterprise?.name!);
      }, ZendeskUtils.SHOW_WIDGET_DELAY);
    }
  };

  getContextInfo = async (contextId?: string) => {
    await this.validateDelegateToken();

    const contextUser = await this.props.getMe(contextId);

    if (!contextUser) {
      return;
    }

    const { remaining } = contextUser;

    this.props.setRateLimits(remaining);
  };

  withBackground = () => {
    const {
      location: { pathname },
      comparison,
      skipEmptyState,
      user,
    } = this.props;
    const isLightTheme = user?.preferences?.theme === ThemeMode.LIGHT;

    const showEmptyStateComparison =
      comparison && !comparison.items.length && pathname.includes(NAV_PATHS.COMPARISON) && !pathname.includes(NAV_PATHS.COMPARISONS) && !skipEmptyState;
    const noGifContact = !isLightTheme && comparison && pathname.endsWith(`${COMPARISON_NAV_PATHS.GIF}`) && !comparison.contact;
    const skipEmptyStateNoItem =
      !isLightTheme && comparison && !comparison.items.length && skipEmptyState && pathname.includes(`${COMPARISON_NAV_PATHS.PERFORMANCE}`);

    return pathname.includes(NAV_PATHS.INSIGHTS) || showEmptyStateComparison || noGifContact || skipEmptyStateNoItem;
  };

  isFullHeight = () => {
    const {
      location: { pathname },
      user,
    } = this.props;

    // Currently removing the "NAV_PATHS.CONTACTS" from the FULL_HEIGHT_PAGES array while in LightMode, to avoid the fullHeight styles from being applied
    // TODO: Once Legacy is deprecated, NAV_PATHS.CONTACTS should be removed from the FULL_HEIGHT_PAGES array at the top of this component
    const { isLightVersion } = getThemeVersion(user);
    if (isLightVersion) {
      const indexOfContactPage = this.FULL_HEIGHT_PAGES.indexOf(NAV_PATHS.CONTACTS);
      if (indexOfContactPage !== -1) {
        this.FULL_HEIGHT_PAGES.splice(indexOfContactPage, 1);
      }
    }

    return this.FULL_HEIGHT_PAGES.some(path => pathname.includes(path));
  };

  handleTheme = async (prevProps: Props, mode: AppMode) => {
    const { userPreferences, user } = this.props;
    const prevMode = prevProps.user?.appConfig.mode;
    const prevTheme = prevProps.userPreferences?.theme;
    const userTheme = userPreferences?.theme;

    // TODO: this condition will trigger the light theme when the appMode is factSet
    // and it needs to be reviewed after enable a future themes implementation
    let isArtificialLightMode = false;
    if (
      mode === AppMode.FACT_SET &&
      (!prevMode || prevMode !== AppMode.FACT_SET) &&
      (!prevTheme || prevTheme !== userTheme) &&
      (!userTheme || userTheme !== ThemeMode.LIGHT)
    ) {
      isArtificialLightMode = true;
      this.props.updateTheme(ThemeMode.LIGHT);
    }

    if (isArtificialLightMode || ((!prevTheme || prevTheme !== userTheme) && userTheme === ThemeMode.LIGHT)) {
      const accessibleUI = userHasPermission(user, PERMISSIONS.ACCESSIBLE_UI);
      overrideComparisonItemColors(ITEM_COLORS_THEME_LIGHT);
      overrideDefaultRiskColors(accessibleUI ? ACCESSIBLE_RISK_COLORS : RISK_COLORS_THEME_LIGHT);
    }
  };

  render() {
    const { user, isPinned, contact } = this.props;
    const mode = user?.appConfig.mode;
    const { initialized } = this.state;

    if (user === undefined || !initialized) {
      return <></>;
    }

    const classes = classNames('app-container', {
      responsive: mode === AppMode.FACT_SET,
      'pinned-container': isPinned,
      'with-background': this.withBackground(),
      'full-height': this.isFullHeight(),
    });

    /**
     * Function to render a private route component.
     * @param path The path of the route.
     * @param component The component to render for the route.
     * @param permissions Optional. An array of permission strings required to access the route.
     * @param exact Optional. A boolean indicating whether the route should match exactly.
     * @param isOneOfManyPermissions Optional. A boolean indicating whether at least one of the permissions is required.
     * @returns A ReactNode representing the rendered private route.
     */
    const renderPrivateRoute = (path: string, component: any, permissions?: string[], exact?: boolean, isOneOfManyPermissions?: boolean) => {
      // Slightly unintuitive, but we only want to render null if the user is authenticated
      // and doesn't have the permission. If they're not authenticated, the route itself
      // will redirect to login - no need to care here.

      if (permissions && user && (isOneOfManyPermissions ? !userHasPermissionEnabled(user, ...permissions) : !userHasPermissions(user, permissions))) {
        return null;
      }

      return <PrivateRoute user={user} path={path} component={component} exact={exact} />;
    };

    const handleDefaultRedirect = () => {
      switch (user?.appConfig.mode) {
        case AppMode.CONTACT: {
          return (
            <Redirect
              from="/"
              to={NAV_PATHS.CONTACT_DETAILS.replace(':contactId', contact != null && contact.contact._id != null ? contact.contact._id : '')}
            />
          );
        }
        case AppMode.LINK: {
          return <Redirect from="/" to={NAV_PATHS.COMPARISON} />;
        }
        case AppMode.ADMIN: {
          return <Redirect from="/" to={NAV_PATHS.ADMIN} />;
        }
        default: {
          return <Redirect from="/" to={NAV_PATHS.COMPARISONS} />;
        }
      }
    };

    const getThemeClassName = () => {
      const {
        location: { pathname },
      } = this.props;
      const preferences = user?.preferences ?? { theme: ThemeMode.DEFAULT };

      return `theme-${getTheme({ pathname, preferences })}`;
    };

    const accessibleUI = userHasPermission(user, PERMISSIONS.ACCESSIBLE_UI);

    const children = (
      <AppProvider>
        <ExternalNotificationCenterProvider>
          <BodyClassManager className="accessible-ui" enabled={accessibleUI} />
          <PostMessage user={user} isPostMessageActive={this.props.isPostMessageActive} />
          <div className={classNames(`h-100 ${getThemeClassName()}`, { 'd-flex': isPinned })}>
            <div className={classes}>
              <RouteListener />
              <PerfectScrollbar className="app-scrollbar-container">
                <CompatRouter>
                  <Switch>
                    <Route path={NAV_PATHS.AUTH} component={Auth} />
                    <Route path={NAV_PATHS.ERROR} component={Error} />
                    <Route path={NAV_PATHS.SSO_COMPLETE} component={SSOComplete} />

                    {renderPrivateRoute(NAV_PATHS.OAUTH2, OAuth2)}
                    {renderPrivateRoute(NAV_PATHS.OAUTH2_SUCCESS, OAuth2Success)}
                    {renderPrivateRoute(NAV_PATHS.INSIGHTS, Insights, [PERMISSIONS.SECTION_INSIGHTS])}
                    {renderPrivateRoute(NAV_PATHS.ADMIN, Admin, ENTERPRISE_ADMIN_PERMISSIONS, undefined, true)}
                    {renderPrivateRoute(NAV_PATHS.CONTACTS, Contacts, [PERMISSIONS.CONTACTS_LIST], true)}
                    {renderPrivateRoute(NAV_PATHS.CONTACT_DETAILS, ContactDetails, [PERMISSIONS.CONTACTS_DETAILS])}
                    {renderPrivateRoute(NAV_PATHS.HOUSEHOLD_CONTACT_DETAILS, HouseholdContactDetails, [PERMISSIONS.HOUSEHOLDS])}
                    {renderPrivateRoute(NAV_PATHS.HOUSEHOLD_DETAILS, HouseholdLanding, [PERMISSIONS.HOUSEHOLDS])}
                    {renderPrivateRoute(NAV_PATHS.SEARCH, Search, [PERMISSIONS.SECTION_SEARCH])}
                    {renderPrivateRoute(NAV_PATHS.MODELS, Models, [PERMISSIONS.SECTION_MODELS])}
                    {renderPrivateRoute(NAV_PATHS.COMPARISON, ComparisonBuilder)}
                    {renderPrivateRoute(NAV_PATHS.COMPARISONS, Comparisons, [PERMISSIONS.SECTION_COMPARISONS])}
                    {renderPrivateRoute(NAV_PATHS.PROPOSAL, ProposalTemplateRouter)}
                    {renderPrivateRoute(NAV_PATHS.SETTINGS, Settings, [PERMISSIONS.SECTION_SETTINGS])}
                    {renderPrivateRoute(NAV_PATHS.ANNOUNCEMENTS, Announcements, [PERMISSIONS.ENTERPRISE_ADMIN_NOTIFICATION_CENTER])}
                    {renderPrivateRoute(NAV_PATHS.PLANNING_IMPORT_ACCOUNTS, ImportAccountsPage, [PERMISSIONS.PLANNING])}
                    {renderPrivateRoute(NAV_PATHS.PLANNING_ASSIGN_CONTACT, AssignContactPage, [PERMISSIONS.PLANNING])}
                    {renderPrivateRoute(NAV_PATHS.PLANNING_ERROR, ErrorPage, [PERMISSIONS.PLANNING])}

                    <DevelopmentRoute user={user} path="/tests" component={TestsRouter} />

                    {handleDefaultRedirect()}
                  </Switch>
                </CompatRouter>
                <Enable2FAModal show={this.props.showTwoFactorModal} onCancel={this.props.hideModal} onDone={this.props.hideModal} />
                {userHasPermission(user, PERMISSIONS.NOTIFICATION_CENTER) && <NotificationCenter />}
                <DevToolPanel />
              </PerfectScrollbar>
            </div>

            <ConnectedPrivateComponent>{() => <PinnedEditPortfolio />}</ConnectedPrivateComponent>
          </div>
        </ExternalNotificationCenterProvider>
      </AppProvider>
    );

    const statsigClient = getStatsigClient();
    if (!statsigClient) {
      return children;
    }
    return <StatsigProvider client={statsigClient}>{children}</StatsigProvider>;
  }
}

const mapStateToProps = (state: ReduxState): StateProps => {
  const currentComparison = state.comparison.activeComparison;

  return {
    user: state.auth.user,
    locale: state.intl.locale,
    isPinned: state.comparison.portfolioEditor.isPinned,
    comparison: currentComparison,
    skipEmptyState: state.comparison.skipEmptyState,
    enterprise: state.enterprise.details,
    context: state.auth.context,
    contact: state.contacts.current,
    taxRates: state.taxes.taxRates,
    showTwoFactorModal: (state.auth.user && state.twoFactorAuth.showModal) || false,
    userPreferences: state.auth.user?.preferences,
    isPostMessageActive: state.postMessage.isPostMessageActive,
  };
};

export default withRouter(
  connect(mapStateToProps, {
    getMe,
    getSavedFunds,
    validateUser,
    setEnterpriseDetails,
    getSavedBenchmarks,
    setRateLimits,
    getContactById,
    loadContactData,
    setLocale,
    addMessages,
    loadTaxes,
    setIsActivePostMessage,
    setIsActiveContactPostMessage,
    setIsActivePortfolioPostMessage,
    setSelectedDelegateAccess,
    validateDelegateAccessToken,
    updatePreferences,
    updateTheme,
    showModal,
    hideModal,
  })(App),
);
